import React, { useEffect, useState, useMemo } from 'react'
import { useAuth } from '../../components/AuthProvider'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { safeJson } from '../../lib/utils'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  Legend
} from 'recharts'
import { componentScope } from '../../lib/cssScope'
import { Select } from '@/components/ui/Select'
import { SecurityCard } from '@/components/ui/SecurityCard'
import { USE_MOCK_TPSM_INVENTORY_DATA } from '../../mocks/useMockTpsmInventory'
import tpsmSuppliersFixture from '../../mocks/fixtures/tpsm-suppliers.json'
import { fetchTpsmAssessmentsForSupplier } from '../../mocks/fetchTpsmAssessmentsForSupplier'

type Assessment = {
  id: string
  standardCode: string
  status: string
  complianceScore: number
  assessedAt?: string
  updatedAt?: string
  createdAt?: string
  findings?: any
}

type Category = {
  code: string
  name: string
  complianceScore: number
}

export type SupplierComplianceSummaryPanelProps = {
  supplierId: string
  supplierName: string
  /** When false, content is not rendered and data is not fetched */
  enabled?: boolean
  /** Hide page title block (e.g. when wrapped in a modal that already has a header) */
  hideHeading?: boolean
}

const KPI_COLORS = {
  green: "#10b981",
  yellow: "#facc15",
  red: "#ef4444",
} as const

const SCORE_COLORS: Record<string, string> = {
  "1": "#9ca3af",
  "2": "#ef4444",
  "3": "#3b82f6",
  "4": "#facc15",
  "5": "#10b981",
}

const clampPercent = (value: number | null | undefined): number => {
  if (value == null || Number.isNaN(value)) return 0
  return Math.max(0, Math.min(100, Math.round(Number(value))))
}

const mapThresholdColor = (value: number): string => {
  if (value > 90) return KPI_COLORS.green
  if (value >= 60) return KPI_COLORS.yellow
  return KPI_COLORS.red
}

const humanize = (value: string): string =>
  value
    .replace(/[_\s]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim()

export const SupplierComplianceSummaryPanel: React.FC<SupplierComplianceSummaryPanelProps> = ({
  supplierId,
  supplierName,
  enabled = true,
  hideHeading = false,
}) => {
  const { token } = useAuth()
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [supplierStandards, setSupplierStandards] = useState<string[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [categorySummaryData, setCategorySummaryData] = useState<Array<{ code: string; name: string; percent: number }>>([])
  
  // Filters
  const [selectedStandard, setSelectedStandard] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  useEffect(() => {
    if (enabled && supplierId) {
      fetchAssessments()
    }
  }, [enabled, supplierId, token])

  useEffect(() => {
    if (enabled && selectedStandard && token) {
      fetchCategorySummary()
    } else {
      setCategorySummaryData([])
    }
  }, [enabled, selectedStandard, token])

  const fetchAssessments = async () => {
    setLoading(true)
    try {
      const raw = await fetchTpsmAssessmentsForSupplier(supplierId, token)
      const assessmentsList = Array.isArray(raw) ? (raw as Assessment[]) : []
      setAssessments(assessmentsList)

      let foundSupplier: { standards?: string[] } | null = null
      if (USE_MOCK_TPSM_INVENTORY_DATA) {
        const list = (tpsmSuppliersFixture as { suppliers?: Array<{ id: string; standards?: string[] }> }).suppliers
        foundSupplier = Array.isArray(list) ? list.find((s) => s.id === supplierId) ?? null : null
      } else {
        const suppliersRes = await fetch(API_ENDPOINTS.SUPPLIERS, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const suppliersData = await safeJson(suppliersRes)
        foundSupplier = Array.isArray(suppliersData.suppliers) 
          ? suppliersData.suppliers.find((s: any) => s.id === supplierId) ?? null
          : null
      }
      
      if (foundSupplier && Array.isArray(foundSupplier.standards)) {
        setSupplierStandards(foundSupplier.standards)
        // Auto-select first standard if none selected
        if (!selectedStandard && foundSupplier.standards.length > 0) {
          setSelectedStandard(foundSupplier.standards[0])
        }
      } else {
        // Fallback: get unique standards from assessments
        const uniqueStandards = new Set(assessmentsList.map((a: Assessment) => a.standardCode))
        const standardsArray = Array.from(uniqueStandards)
        setSupplierStandards(standardsArray)
        // Auto-select first standard if none selected
        if (!selectedStandard && standardsArray.length > 0) {
          setSelectedStandard(standardsArray[0])
        }
      }

      // Extract categories from assessments
      const categoriesMap = new Map<string, Category>()
      assessmentsList.forEach((assessment: Assessment) => {
        if (assessment.findings?.metadata?.categories) {
          Object.entries(assessment.findings.metadata.categories).forEach(([catCode, catData]: [string, any]) => {
            if (!categoriesMap.has(catCode)) {
              categoriesMap.set(catCode, {
                code: catCode,
                name: catData.name || humanize(catCode),
                complianceScore: 0
              })
            }
            const category = categoriesMap.get(catCode)!
            category.complianceScore = Math.max(category.complianceScore, catData.complianceScore || 0)
          })
        }
      })
      setCategories(Array.from(categoriesMap.values()))
    } catch (e: any) {
      console.error('Failed to fetch assessments:', e)
      setAssessments([])
      setSupplierStandards([])
    } finally {
      setLoading(false)
    }
  }

  const fetchCategorySummary = async () => {
    if (!selectedStandard) {
      setCategorySummaryData([])
      return
    }
    
    try {
      const params = new URLSearchParams()
      params.set('standard', selectedStandard)
      
      const res = await fetch(`${API_ENDPOINTS.COMPLIANCE_CATEGORIES_SUMMARY}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await safeJson(res)
      const summary = Array.isArray(data?.data) ? data.data : []
      setCategorySummaryData(summary)
    } catch (e: any) {
      console.error('[SupplierComplianceSummaryPanel] Failed to fetch category summary:', e)
      setCategorySummaryData([])
    }
  }

  // Filter assessments by selected standard and category
  const filteredAssessments = useMemo(() => {
    return assessments.filter(a => {
      if (selectedStandard && a.standardCode !== selectedStandard) return false
      if (selectedCategory !== 'all') {
        // Check if assessment has the selected category
        const hasCategory = a.findings?.metadata?.categories?.[selectedCategory]
        if (!hasCategory) return false
      }
      return true
    })
  }, [assessments, selectedStandard, selectedCategory])

  // Get available categories for selected standard
  const availableCategories = useMemo(() => {
    if (!selectedStandard) {
      // If no standard selected, show all categories
      return categories
    }
    // Filter categories that exist in assessments for selected standard
    const standardCategories = new Set<string>()
    assessments
      .filter(a => a.standardCode === selectedStandard)
      .forEach(a => {
        if (a.findings?.metadata?.categories) {
          Object.keys(a.findings.metadata.categories).forEach(catCode => {
            standardCategories.add(catCode)
          })
        }
      })
    return categories.filter(c => standardCategories.has(c.code))
  }, [categories, assessments, selectedStandard])

  // Calculate KPI metrics
  const kpiCards = useMemo(() => {
    // Get unique standards from filtered assessments with scores
    const standardsWithScores = new Map<string, number>()
    filteredAssessments.forEach(a => {
      const code = a.standardCode
      const score = a.complianceScore || 0
      // Keep the highest score for each standard
      if (!standardsWithScores.has(code) || standardsWithScores.get(code)! < score) {
        standardsWithScores.set(code, score)
      }
    })

    // Calculate average compliance across unique standards
    const standardScores = Array.from(standardsWithScores.values())
    const avgCompliance = standardScores.length > 0
      ? Math.round(standardScores.reduce((sum, score) => sum + score, 0) / standardScores.length)
      : 0

    // Active standards = standards with assessments (any status)
    const activeStandards = standardsWithScores.size
    // Total standards = standards assigned to supplier
    const totalStandards = supplierStandards.length > 0 ? supplierStandards.length : activeStandards
    const lastUpdated = filteredAssessments.length > 0
      ? filteredAssessments.sort((a, b) => 
          new Date(b.updatedAt || b.assessedAt || b.createdAt || 0).getTime() - 
          new Date(a.updatedAt || a.assessedAt || a.createdAt || 0).getTime()
        )[0]?.updatedAt || filteredAssessments[0]?.assessedAt || filteredAssessments[0]?.createdAt
      : null
    
    const recencyPercent = lastUpdated
      ? clampPercent(100 - (Math.min(Math.round((Date.now() - new Date(lastUpdated).getTime()) / (1000 * 60 * 60 * 24)), 180) / 180) * 100)
      : 0

    const auditStatus = avgCompliance >= 80 ? 'COMPLIANT' : avgCompliance >= 40 ? 'PARTIAL' : 'NON-COMPLIANT'
    const auditPercent = avgCompliance >= 80 ? 100 : avgCompliance >= 40 ? 75 : 40

    return [
      {
        id: "totalHealth",
        title: t("compliance.kpi.totalHealth", "Overall Compliance"),
        primary: `${avgCompliance}%`,
        percent: avgCompliance,
        subtitle: t("compliance.kpi.totalHealthHint", "Average across assessed standards"),
        color: mapThresholdColor(avgCompliance)
      },
      {
        id: "activeStandards",
        title: t("compliance.kpi.activeStandards", "Active Standards"),
        primary: `${activeStandards}/${totalStandards || "—"}`,
        percent: totalStandards > 0 ? clampPercent((activeStandards / totalStandards) * 100) : 0,
        subtitle: t("compliance.kpi.activeStandardsHint", "Currently tracking compliance"),
        color: mapThresholdColor(totalStandards > 0 ? (activeStandards / totalStandards) * 100 : 0)
      },
      {
        id: "auditReadiness",
        title: t("compliance.kpi.auditReadiness", "Audit Readiness"),
        primary: humanize(auditStatus),
        percent: auditPercent,
        subtitle: t("compliance.kpi.auditReadinessHint", "Based on latest assessments"),
        color: mapThresholdColor(auditPercent)
      },
      {
        id: "lastUpdated",
        title: t("compliance.kpi.lastUpdated", "Last Updated"),
        primary: lastUpdated ? new Date(lastUpdated).toLocaleDateString() : t("compliance.kpi.lastUpdatedUnknown", "No data"),
        percent: recencyPercent,
        subtitle: t("compliance.kpi.lastUpdatedHint", "Higher is more recent (last 180 days)"),
        color: mapThresholdColor(recencyPercent)
      }
    ]
  }, [filteredAssessments, supplierStandards, t])

  // Compliance by Standard data
  const complianceByStandardData = useMemo(() => {
    const seen = new Map<string, { code: string; name: string; value: number }>()
    filteredAssessments.forEach((assessment) => {
      const code = assessment.standardCode
      const value = clampPercent(assessment.complianceScore || 0)
      if (!seen.has(code) || seen.get(code)!.value < value) {
        seen.set(code, {
          code,
          name: humanize(code),
          value
        })
      }
    })
    return Array.from(seen.values()).sort((a, b) => a.value - b.value)
  }, [filteredAssessments])

  // Trend over time data
  const trendData = useMemo(() => {
    const sorted = [...filteredAssessments]
      .filter(a => a.assessedAt || a.updatedAt || a.createdAt)
      .sort((a, b) => {
        const dateA = new Date(a.assessedAt || a.updatedAt || a.createdAt || 0).getTime()
        const dateB = new Date(b.assessedAt || b.updatedAt || b.createdAt || 0).getTime()
        return dateA - dateB
      })
    
    // Group by month
    const monthly: Record<string, { count: number; total: number }> = {}
    sorted.forEach(a => {
      const date = new Date(a.assessedAt || a.updatedAt || a.createdAt || 0)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      if (!monthly[monthKey]) {
        monthly[monthKey] = { count: 0, total: 0 }
      }
      monthly[monthKey].count++
      monthly[monthKey].total += a.complianceScore || 0
    })
    
    return Object.entries(monthly)
      .map(([key, data]) => ({
        label: key,
        value: Math.round(data.total / data.count)
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [filteredAssessments])

  // Score distribution (1-5 scale)
  const scoreDistribution = useMemo(() => {
    const distribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    filteredAssessments.forEach(a => {
      const score = a.complianceScore || 0
      let bucket = 1
      if (score >= 80) bucket = 5
      else if (score >= 60) bucket = 4
      else if (score >= 40) bucket = 3
      else if (score >= 20) bucket = 2
      distribution[bucket]++
    })
    return Object.entries(distribution).map(([score, count]) => ({
      score: Number(score),
      count
    }))
  }, [filteredAssessments])

  // Radar data (maturity map) - use compliance by standard data
  const radarData = useMemo(() => {
    return complianceByStandardData.map(item => ({
      subject: item.name.length > 15 ? item.name.substring(0, 12) + '...' : item.name,
      fullName: item.name,
      value: item.value
    }))
  }, [complianceByStandardData])

  // Category radar data for selected standard
  const categoryRadarData = useMemo(() => {
    if (!selectedStandard) {
      return []
    }
    
    // Priority 1: Use categorySummaryData from API (most reliable)
    if (categorySummaryData.length > 0) {
      return categorySummaryData.map(item => ({
        subject: item.name.length > 15 ? item.name.substring(0, 12) + '...' : item.name,
        fullName: item.name,
        value: clampPercent(item.percent || 0)
      }))
    }
    
    // Priority 2: Get categories from assessments findings
    const standardCategories = new Map<string, { name: string; score: number }>()
    
    filteredAssessments
      .filter(a => a.standardCode === selectedStandard)
      .forEach(assessment => {
        if (assessment.findings?.metadata?.categories) {
          Object.entries(assessment.findings.metadata.categories).forEach(([catCode, catData]: [string, any]) => {
            const existing = standardCategories.get(catCode)
            const score = clampPercent(catData.complianceScore || 0)
            if (!existing || existing.score < score) {
              standardCategories.set(catCode, {
                name: catData.name || humanize(catCode),
                score
              })
            }
          })
        }
      })
    
    // Priority 3: If no categories found in findings, try to get from availableCategories
    if (standardCategories.size === 0 && availableCategories.length > 0) {
      availableCategories.forEach(cat => {
        if (!standardCategories.has(cat.code)) {
          standardCategories.set(cat.code, {
            name: cat.name,
            score: clampPercent(cat.complianceScore || 0)
          })
        }
      })
    }
    
    // Priority 4: If still no categories, try to get from all categories filtered by standard
    if (standardCategories.size === 0 && categories.length > 0) {
      categories.forEach(cat => {
        // Check if this category appears in any assessment for this standard
        const hasCategory = assessments.some(a => 
          a.standardCode === selectedStandard && 
          a.findings?.metadata?.categories?.[cat.code]
        )
        if (hasCategory && !standardCategories.has(cat.code)) {
          standardCategories.set(cat.code, {
            name: cat.name,
            score: clampPercent(cat.complianceScore || 0)
          })
        }
      })
    }
    
    return Array.from(standardCategories.values()).map(item => ({
      subject: item.name.length > 15 ? item.name.substring(0, 12) + '...' : item.name,
      fullName: item.name,
      value: item.score
    }))
  }, [selectedStandard, filteredAssessments, availableCategories, categories, assessments, categorySummaryData])

  // Weak areas (lowest 5) - use compliance by standard data
  const weakAreas = useMemo(() => {
    return [...complianceByStandardData]
      .sort((a, b) => a.value - b.value)
      .slice(0, 5)
      .map(item => ({
        name: item.name.length > 25 ? item.name.substring(0, 22) + '...' : item.name,
        fullName: item.name,
        value: item.value
      }))
  }, [complianceByStandardData])

  const formatPercent = (value: number) => `${clampPercent(value)}%`

  /** Recharts needs a pixel height; wrapper gets overflow:auto so axes wider than card scroll inside */
  const CHART_PX = 288
  const chartCardClass =
    'ds-card flex w-[min(100%,400px)] min-w-[280px] shrink-0 flex-col p-4 sm:min-w-[320px]'
  const chartScrollClass = 'min-h-[288px] w-full min-w-0 overflow-auto'

  if (!enabled) {
    return null
  }

  return (
    <div className={componentScope('SupplierComplianceSummaryPanel')}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600 mx-auto mb-2" />
                <span className="text-sm text-gray-600">{t('common.loading', 'Loading...')}</span>
              </div>
            </div>
          ) : (
            <>
              {!hideHeading && (
                <div className="mb-2">
                  <h2 className="text-lg font-semibold text-[var(--ds-text,#111827)]">
                    {t('tpsm.supplierProfile.complianceSummary', 'Compliance Summary')}
                    {supplierName ? ` — ${supplierName}` : ''}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--ds-text-secondary,#6b7280)]">
                    {t('tpsm.supplierProfile.complianceSummaryDesc', 'Detailed compliance analytics and metrics')}
                  </p>
                </div>
              )}

              {/* Filters */}
              <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-3xl">
                <Select
                  label={t('compliance.filters.standard', 'Standard')}
                  options={[
                    { value: '', label: t('compliance.filters.allStandards', 'All Standards') },
                    ...supplierStandards.map((std) => ({ value: std, label: humanize(std) })),
                  ]}
                  value={selectedStandard}
                  onChange={(v) => setSelectedStandard(v ?? '')}
                  placeholder={t('compliance.filters.standard', 'Standard')}
                  minWidth="100%"
                />
                <Select
                  label={t('compliance.filters.category', 'Category')}
                  options={[
                    { value: 'all', label: t('compliance.filters.allCategories', 'All Categories') },
                    ...availableCategories.map((cat) => ({ value: cat.code, label: cat.name })),
                  ]}
                  value={selectedCategory}
                  onChange={(v) => v != null && setSelectedCategory(v)}
                  placeholder={t('compliance.filters.category', 'Category')}
                  minWidth="100%"
                />
              </div>

              {/* KPI — SecurityCard: one row, scroll on narrow viewports (aligned with Home flex + gap) */}
              <div className="mb-4 flex flex-row flex-nowrap items-stretch gap-4 overflow-x-auto pb-1 [scrollbar-width:thin]">
                {kpiCards.map((card) => (
                  <SecurityCard
                    key={card.id}
                    className="!p-2.5 min-h-[100px] min-w-[168px] max-w-[200px] shrink-0"
                    title={card.title}
                    percent={card.percent}
                    color={card.color}
                    size="small"
                    chartSize={72}
                    innerRadius={26}
                    outerRadius={32}
                    hoverDetail={
                      <div className="space-y-1 text-left text-sm">
                        <div className="font-medium text-[var(--ds-text,#111827)]">{card.primary}</div>
                        <div className="text-xs text-[var(--ds-text-secondary,#6b7280)]">{card.subtitle}</div>
                      </div>
                    }
                  />
                ))}
              </div>

              {/* Charts row — outer scroll-x (MainGlobal .charts-row); inner overflow:auto per chart */}
              <div className="charts-row w-full min-w-0">
                {/* Trend Over Time */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.trend", "Compliance Trend (Over Time)")}
                  </h2>
                  <div className={chartScrollClass}>
                    {trendData.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <LineChart data={trendData} margin={{ top: 12, right: 24, bottom: 8, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Compliance by Standard */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.byStandard", "Compliance by Standard")}
                  </h2>
                  <div className={chartScrollClass}>
                    {complianceByStandardData.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <BarChart data={complianceByStandardData} layout="vertical" margin={{ top: 12, right: 24, bottom: 8, left: 35 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Bar dataKey="value" fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Score Distribution */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.distribution", "Distribution by Score (1–5)")}
                  </h2>
                  <div className={chartScrollClass}>
                    {scoreDistribution.every(s => s.count === 0) ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <BarChart data={scoreDistribution} margin={{ top: 12, right: 24, bottom: 8, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="score" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#3b82f6">
                            {scoreDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={SCORE_COLORS[String(entry.score)] || "#3b82f6"} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Maturity Map (Radar) */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.maturityMap", "Control Maturity Map")}
                  </h2>
                  <div className={chartScrollClass}>
                    {radarData.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <RadarChart data={radarData}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <Radar
                            name="Compliance"
                            dataKey="value"
                            stroke="#3b82f6"
                            fill="#3b82f6"
                            fillOpacity={0.6}
                          />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Category Maturity Map (Radar) */}
                <div className={chartCardClass} id="category-maturity-map-chart">
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {selectedStandard
                      ? `${humanize(selectedStandard)} — ${t('compliance.charts.categoryMaturity', 'Category Maturity Map')}`
                      : t('compliance.charts.categoryMaturity', 'Category Maturity Map')}
                  </h2>
                  <div className={chartScrollClass}>
                    {!selectedStandard ? (
                      <div className="flex h-[288px] items-center justify-center px-3 text-center text-sm text-[var(--ds-text-secondary,#6b7280)]">
                        {t('compliance.charts.selectStandard', 'Select a standard to view category maturity')}
                      </div>
                    ) : categoryRadarData.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center px-3 text-center text-sm text-[var(--ds-text-secondary,#6b7280)]">
                        {t('common.noData', 'No data')}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <RadarChart data={categoryRadarData}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <Radar
                            name="Compliance"
                            dataKey="value"
                            stroke="var(--ds-primary, #56a3d9)"
                            fill="var(--ds-primary, #56a3d9)"
                            fillOpacity={0.35}
                          />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Weak Areas */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.weakAreas", "Weak Areas (Lowest 5)")}
                  </h2>
                  <div className={chartScrollClass}>
                    {weakAreas.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <BarChart data={weakAreas} layout="vertical" margin={{ top: 12, right: 24, bottom: 8, left: 80 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Bar dataKey="value" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
    </div>
  )
}

